import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Timer, ChevronDown, ChevronUp, Dumbbell, Info,
} from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import MuscleDiagram from "./MuscleDiagram";
import HoldTimer from "./HoldTimer";
import SetList from "./SetList";
import ExerciseInfoModal from "./ExerciseInfoModal";
import { haptic } from "../lib/haptics";
import { notifyBadgeUnlock } from "../lib/badges";

/* -------------------- exercise classification helpers -------------------- */

const inferHold = (ex) => ex.is_static_hold === true;

const BODYWEIGHT_NAME_RE = /\b(push.?up|pull.?up|chin.?up|dip|plank|burpee|sit.?up|crunch|mountain.?climber|jumping.?jack|bodyweight|hollow.?hold|hanging|lunge|squat \(bodyweight\)|inverted.?row|pistol.?squat|l.?sit|handstand|toes.?to.?bar|leg.?raise|superman|bicycle.?crunch|russian.?twist)/i;

const inferBodyweight = (ex) => {
  if (ex.is_bodyweight === true) return true;
  if (ex.is_bodyweight === false) return false;
  return BODYWEIGHT_NAME_RE.test(ex.name || "");
};

const prescribedHoldSec = (ex) => {
  if (ex.hold_seconds && ex.hold_seconds > 0) return Number(ex.hold_seconds);
  const m = String(ex.reps || "").match(/(\d+)\s*(s|sec)\b/i);
  if (m) return Number(m[1]);
  return 30;
};

/* -------------------- component -------------------- */

export default function ExerciseCard({ exercise, dayIndex, exIndex, defaultExpanded = false, compact = false }) {
  const isHold = inferHold(exercise);
  const isBodyweight = inferBodyweight(exercise);
  const holdSec = prescribedHoldSec(exercise);
  const targetSets = Math.max(1, Number(exercise.sets) || 3);
  const restSec = Math.max(15, Number(exercise.rest_sec) || 60);

  const [expanded, setExpanded] = useState(!!defaultExpanded);
  const [history, setHistory] = useState([]);
  const [timerOpen, setTimerOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  const loadHistory = async () => {
    try {
      const { data } = await api.get(
        `/workouts/exercise-history?exercise_name=${encodeURIComponent(exercise.name)}&weeks=8`
      );
      setHistory(data.weeks || []);
    } catch (err) {
      console.error("Failed to load exercise history:", err);
    }
  };

  useEffect(() => {
    if (expanded && history.length === 0) loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  const logHold = async (sec) => {
    try {
      const { data } = await api.post("/workouts/log-set", {
        exercise_name: exercise.name,
        muscle_group: exercise.muscle_group,
        sets: [],
        hold_seconds: sec,
      });
      notifyBadgeUnlock(data?.badge_unlocked);
    } catch (err) {
      console.error("Failed to log hold:", err);
      toast.error("Failed to log hold");
    }
  };

  const handleHoldSetComplete = async (seconds, setIdx) => {
    await logHold(seconds);
    haptic.tap();
    toast.success(`Set ${setIdx + 1} · ${seconds}s logged`);
  };

  const handleAllHoldsComplete = async (perSetSeconds) => {
    const total = perSetSeconds.reduce((a, b) => a + b, 0);
    haptic.success();
    toast.success(`All ${perSetSeconds.length} sets done · ${total}s total`);
    await loadHistory();
  };

  // Last week comparison (second-to-last bucket)
  const lastWeek = history.length >= 2 ? history[history.length - 2] : null;

  const formatLastWeek = () => {
    if (!lastWeek) return "—";
    if (isHold) return lastWeek.best_hold ? `${lastWeek.best_hold}s` : "—";
    if (!lastWeek.top_weight && !lastWeek.top_reps) return "—";
    if (isBodyweight) return `${lastWeek.top_reps} reps`;
    return `${lastWeek.top_weight}kg × ${lastWeek.top_reps}`;
  };
  const lastWeekLabel = formatLastWeek();

  const chartMetric = (w) => {
    if (isHold) return w.best_hold;
    if (isBodyweight) return w.top_reps;
    return w.top_weight;
  };
  const chartData = history.map((w) => ({
    label: new Date(w.week_start).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    value: chartMetric(w),
  }));
  const hasChartData = chartData.some((d) => d.value > 0);

  const progressMetricLabel = () => {
    if (isHold) return "— best hold (s)";
    if (isBodyweight) return "— top reps";
    return "— top weight (kg)";
  };

  return (
    <>
      <div className="border border-white/10 bg-[#121212]" data-testid={`exercise-card-${dayIndex}-${exIndex}`}>
        <button
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-center gap-4 p-4 text-left hover:bg-white/5 transition-colors"
          data-testid={`exercise-toggle-${dayIndex}-${exIndex}`}
        >
          <Dumbbell className="w-4 h-4 text-white/40 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <div className="font-bold text-white truncate">{exercise.name}</div>
              <span
                role="button"
                tabIndex={0}
                aria-label={`How to perform ${exercise.name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  haptic.tap();
                  setInfoOpen(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    setInfoOpen(true);
                  }
                }}
                className="shrink-0 inline-flex items-center justify-center w-7 h-7 -m-1.5 rounded-full text-[#6B9BFF] hover:bg-white/5 active:bg-white/10 transition-colors cursor-pointer touch-manipulation"
                data-testid={`exercise-info-button-${dayIndex}-${exIndex}`}
              >
                <Info className="w-4 h-4" />
              </span>
            </div>
            <div className="text-xs text-white/50 mt-0.5">
              {isHold ? (
                <>{exercise.sets} × {holdSec}s hold · {exercise.rest_sec || 60}s rest</>
              ) : (
                <>{exercise.sets} sets × {exercise.reps} reps · {exercise.rest_sec || 60}s rest</>
              )}
            </div>
          </div>
          {exercise.muscle_group && (
            <span className="hidden sm:inline text-[10px] uppercase tracking-widest text-white/40 border border-white/10 px-2 py-1">
              {exercise.muscle_group}
            </span>
          )}
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-white/40" />
          ) : (
            <ChevronDown className="w-4 h-4 text-white/40" />
          )}
        </button>

        {expanded && (
          <div className={`border-t border-white/5 ${compact ? "p-3 space-y-3" : "p-5 space-y-6"}`}>
            <div className={compact ? "" : "grid grid-cols-1 lg:grid-cols-3 gap-6"}>
              {!compact && (
                <div className="lg:col-span-1 flex flex-col items-center">
                  <MuscleDiagram group={exercise.muscle_group || "full_body"} size={160} />
                  <div className="text-[10px] uppercase tracking-widest text-white/40 mt-3">
                    {exercise.muscle_group || "full body"}
                  </div>
                  {exercise.notes && (
                    <p className="text-xs text-white/60 mt-3 text-center italic">{exercise.notes}</p>
                  )}
                </div>
              )}

              <div className={compact ? "space-y-3" : "lg:col-span-2 space-y-4"}>
                {!compact && (
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="label-eyebrow text-white/50">Last week</div>
                      <div className="font-display text-2xl text-white mt-1">{lastWeekLabel}</div>
                    </div>
                    {isHold && (
                      <button
                        onClick={() => setTimerOpen(true)}
                        className="bg-gradient-to-r from-[#7C3AED] to-[#6B9BFF] text-white font-bold uppercase tracking-wider text-xs px-5 py-3 inline-flex items-center gap-2"
                        data-testid={`hold-timer-open-${dayIndex}-${exIndex}`}
                      >
                        <Timer className="w-4 h-4" />
                        {Number(exercise.sets) > 1
                          ? `Start ${exercise.sets} × ${holdSec}s`
                          : `Start ${holdSec}s timer`}
                      </button>
                    )}
                  </div>
                )}

                {compact && isHold && (
                  <button
                    onClick={() => setTimerOpen(true)}
                    className="w-full bg-gradient-to-r from-[#7C3AED] to-[#6B9BFF] text-white font-bold uppercase tracking-wider text-xs px-4 py-3 inline-flex items-center justify-center gap-2"
                    data-testid={`hold-timer-open-${dayIndex}-${exIndex}`}
                  >
                    <Timer className="w-4 h-4" />
                    {Number(exercise.sets) > 1
                      ? `Start ${exercise.sets} × ${holdSec}s`
                      : `Start ${holdSec}s timer`}
                  </button>
                )}

                {!isHold && (
                  <SetList
                    exerciseName={exercise.name}
                    muscleGroup={exercise.muscle_group}
                    targetSets={targetSets}
                    restSec={restSec}
                    isBodyweight={isBodyweight}
                    dayIndex={dayIndex}
                    exIndex={exIndex}
                    onLogged={loadHistory}
                  />
                )}

                {!compact && (
                  <div>
                    <div className="label-eyebrow text-white/50 mb-2">
                      Progress (last 8 weeks) {progressMetricLabel()}
                    </div>
                    <div className="h-32 border border-white/5 bg-[#0a0a0a] p-2">
                      {hasChartData ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={chartData}>
                            <XAxis dataKey="label" stroke="#71717A" fontSize={10} />
                            <YAxis stroke="#71717A" fontSize={10} width={28} />
                            <Tooltip
                              contentStyle={{
                                background: "#121212",
                                border: "1px solid rgba(255,255,255,0.1)",
                                color: "#fff",
                                fontSize: 12,
                              }}
                            />
                            <Line type="monotone" dataKey="value" stroke="#6B9BFF" strokeWidth={2} dot={{ r: 3 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-full flex items-center justify-center text-white/30 text-xs">
                          Log this week to start your progress chart
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <HoldTimer
        open={timerOpen}
        prescribedSeconds={holdSec}
        restSec={exercise.rest_sec || 60}
        totalSets={Number(exercise.sets) || 1}
        exerciseName={exercise.name}
        onClose={() => setTimerOpen(false)}
        onSetComplete={handleHoldSetComplete}
        onComplete={handleAllHoldsComplete}
      />

      <ExerciseInfoModal
        open={infoOpen}
        exercise={exercise}
        onClose={() => setInfoOpen(false)}
      />
    </>
  );
}
